import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Copy, Download, RefreshCw } from 'lucide-react';

/**
 * QR-code + verification + backup-codes ladder shown while the user is
 * enrolling a new TOTP secret. All state lives in the parent — this is a
 * presentational component only.
 */
export default function MFASetupFlow({
  setupData,
  verifyCode,
  onVerifyCodeChange,
  onVerify,
  backupCodes,
  loading,
  onCopy,
  onDownload,
}) {
  return (
    <div className="space-y-6" data-testid="mfa-setup-flow">
      <div className="flex items-start p-4 bg-amber-50 border border-amber-200 rounded-md">
        <AlertTriangle className="w-5 h-5 text-amber-600 mr-3 mt-0.5" />
        <div className="text-sm text-amber-700">
          <p className="font-medium">Important: save your backup codes</p>
          <p>
            They are shown once, on this screen only. Each code works a single time and lets you
            sign in if the phone holding your authenticator is lost.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* QR Code */}
        <div className="text-center">
          <p className="text-sm font-medium text-slate-700 mb-3">
            1. Scan this QR code with your authenticator app
          </p>
          <div className="inline-block p-4 bg-white border border-slate-200 rounded-lg">
            <img
              src={`data:image/png;base64,${setupData.qr_code}`}
              alt="MFA QR Code"
              className="w-48 h-48"
              data-testid="mfa-qr-code"
            />
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Works with Google Authenticator, Authy, 1Password, etc.
          </p>
        </div>

        {/* Manual Entry + Verify */}
        <div>
          <p className="text-sm font-medium text-slate-700 mb-3">
            Or enter this secret key manually:
          </p>
          <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-md font-mono text-sm">
            <span className="break-all">{setupData.secret}</span>
            <Button variant="ghost" size="sm" onClick={() => onCopy(setupData.secret)}>
              <Copy className="w-4 h-4" />
            </Button>
          </div>

          <div className="mt-6">
            <Label className="text-sm font-medium text-slate-700">
              2. Enter the 6-digit code from your app
            </Label>
            <div className="flex gap-2 mt-2">
              <Input
                type="text"
                maxLength={6}
                placeholder="000000"
                value={verifyCode}
                onChange={(e) => onVerifyCodeChange(e.target.value.replace(/\D/g, ''))}
                className="font-mono text-center text-lg tracking-widest"
                data-testid="mfa-verify-input"
              />
              <Button
                onClick={onVerify}
                disabled={loading || verifyCode.length !== 6}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="verify-mfa-button"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Verify'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Backup Codes */}
      <div className="border-t border-slate-200 pt-6">
        <p className="text-sm font-medium text-slate-700 mb-1">
          3. Save your {backupCodes.length} backup codes
        </p>
        <p className="text-xs text-slate-500 mb-3">
          This is the only time they are displayed. If you lose them, sign in and regenerate a new set.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="setup-backup-codes">
          {backupCodes.map((code, idx) => (
            <div
              key={code || `code-${idx}`}
              className="p-2 bg-slate-100 rounded text-center font-mono text-sm"
            >
              {code}
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onCopy(backupCodes.join('\n'))}
            data-testid="copy-backup-codes-btn"
          >
            <Copy className="w-4 h-4 mr-2" />
            Copy all codes
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDownload(backupCodes)}
            data-testid="download-backup-codes-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Download
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * State shown after MFA is enabled: regenerate codes + disable form.
 */
export function MFAEnabledCard({
  mfaStatus,
  disableCode,
  onDisableCodeChange,
  onDisable,
  onRegenerate,
  loading,
}) {
  return (
    <div className="space-y-6" data-testid="mfa-enabled-card">
      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-md">
        <div>
          <p className="font-medium text-slate-900">Backup Codes Remaining</p>
          <p className="text-sm text-slate-500">
            {mfaStatus.backup_codes_remaining} unused backup code
            {mfaStatus.backup_codes_remaining === 1 ? '' : 's'} left
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onRegenerate}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Regenerate
        </Button>
      </div>

      <div className="border-t border-slate-200 pt-6">
        <h3 className="font-medium text-slate-900 mb-3">Disable Two-Factor Authentication</h3>
        <p className="text-sm text-slate-500 mb-4">
          Enter your current MFA code to disable two-factor authentication. This will make your account less secure.
        </p>
        <div className="flex gap-2">
          <Input
            type="text"
            maxLength={6}
            placeholder="000000"
            value={disableCode}
            onChange={(e) => onDisableCodeChange(e.target.value.replace(/\D/g, ''))}
            className="font-mono text-center w-32"
            data-testid="mfa-disable-input"
          />
          <Button
            variant="destructive"
            onClick={onDisable}
            disabled={loading || disableCode.length !== 6}
            data-testid="disable-mfa-button"
          >
            Disable MFA
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Post-enable banner listing the fresh backup codes.
 */
export function BackupCodesBanner({ backupCodes, onCopy, onDownload, onDismiss }) {
  if (!backupCodes || backupCodes.length === 0) return null;
  return (
    <Card className="border border-emerald-200 bg-emerald-50" data-testid="backup-codes-banner">
      <div className="p-6">
        <div className="flex items-start">
          <div className="flex-1">
            <h3 className="font-semibold text-emerald-900">Your Backup Codes</h3>
            <p className="text-sm text-emerald-700 mb-4">
              Store these somewhere safe. Each code works once, and this is the only time they are shown.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              {backupCodes.map((code, idx) => (
                <div
                  key={code || `code-${idx}`}
                  className="p-2 bg-white border border-emerald-200 rounded text-center font-mono text-sm"
                >
                  {code}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => onCopy(backupCodes.join('\n'))}>
                <Copy className="w-4 h-4 mr-2" />
                Copy All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDownload(backupCodes)}
                data-testid="download-backup-codes-banner-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
              <Button variant="ghost" size="sm" onClick={onDismiss}>
                I&apos;ve saved these codes
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
